import { useEffect, useState } from "react";
import {
  Autocomplete,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { Modal } from "../../../components/common/Modal/Modal";
import { ModalHeader } from "../../../components/common/Modal/ModalHeader";
import { ModalBody } from "../../../components/common/Modal/ModalBody";
import { ModalFooter } from "../../../components/common/Modal/ModalFooter";
import { Formik } from "formik";
import { useCourseWebStore } from "../../../hooks/useCourseWebStore";
import {
  Category,
  Course,
  CourseWebCreateEdit,
  Teacher,
} from "../../../types/slices/coursesWebType";

const MAX_IMAGE_SIZE_KB = 1000;
const ALLOWED_TYPES = ["image/webp", "image/png", "image/jpeg", "image/jpg"];
const today = (() => {
  const peruNow = new Date(
    new Date().toLocaleString("en-US", { timeZone: "America/Lima" }),
  );
  peruNow.setDate(peruNow.getDate() - 1);
  return peruNow.toISOString().split("T")[0];
})();
const DAYS = [
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
  "Domingo",
];

export const CreateEditCourseWebModal: React.FC<any> = ():
  | JSX.Element
  | any => {
  const {
    editCourseWeb,
    openModal,
    isEdit,
    categories,
    courses,
    teachers,
    setOpenModal,
    createCourseWebStore,
    editCourseWebStore,
    getCoursesWeb,
    getTeacher,
    setSelectedTeacher,
    setSelectedCategory,
    getCategory,
    setSelectedCourse,
    getCourse,
    selectedCategory,
    selectedTeacher,
    selectedCourse,
  } = useCourseWebStore();
  const [data, setData] = useState<CourseWebCreateEdit>({
    courses: {
      id: 0,
      name: "",
      code: "",
      n_modules: 0,
      n_academic_hours: 0,
    },
    course_id: 0,
    teachers: {
      id: 0,
      name: "",
      surname: "",
      dni: "",
    },
    teacher_id: 0,
    teacher: "",
    categories: {
      id: 0,
      name: "",
    },
    category_id: 0,
    category: "",
    description: "",
    start_date: today,
    end_date: "",
    link_group_whatsapp: "",
    link_form: "",
    modality: "Virtual",
    slots_available: 1,
    order: 1,
    class_days: [],
    class_schedule: {
      start: "",
      end: "",
    },
    status: "1",
    image: "",
  });

  useEffect(() => {
    getCourse();
    getCategory();
    getTeacher();
    if (isEdit && openModal) {
      setData(editCourseWeb);
      setSelectedCourse(editCourseWeb.courses || ({} as Course));
      setSelectedTeacher(editCourseWeb.teachers || ({} as Teacher));
      setSelectedCategory(editCourseWeb.categories || ({} as Category));
    } else {
      setData({
        courses: {
          id: 0,
          name: "",
          code: "",
          n_modules: 0,
          n_academic_hours: 0,
        },
        course_id: 0,
        teachers: {
          id: 0,
          name: "",
          surname: "",
          dni: "",
        },
        teacher_id: 0,
        teacher: "",
        categories: {
          id: 0,
          name: "",
        },
        category_id: 0,
        category: "",
        description: "",
        start_date: today,
        end_date: "",
        link_group_whatsapp: "",
        link_form: "",
        modality: "Virtual",
        slots_available: 1,
        order: 1,
        class_days: [],
        class_schedule: {
          start: "",
          end: "",
        },
        status: "1",
        image: "",
      });
      setSelectedCourse({} as Course);
      setSelectedTeacher({} as Teacher);
      setSelectedCategory({} as Category);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editCourseWeb, openModal]);

  const validateImage = (file?: File) => {
    if (!file) return "La imagen es obligatoria";
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Formato inválido. Solo se permiten imágenes WEBP, PNG o JPG";
    }
    const sizeKB = file.size / 1024;
    if (sizeKB > MAX_IMAGE_SIZE_KB) {
      return `La imagen es muy pesada. Máximo ${MAX_IMAGE_SIZE_KB} KB`;
    }

    return undefined;
  };

  const validateForm = (values) => {
    let errors: any = {};
    if (!values.teacher && !values.teacher_id)
      errors.teacher_id = "Seleccione un docente";
    if (!values.category && !values.category_id)
      errors.category_id = "Seleccione una categoría";
    if (!values.description) errors.description = "Descripción es requerida";
    if (values.description.length > 300)
      errors.description = "El número máximo de caracteres es 300";
    if (!values.start_date) errors.start_date = "Fecha de inicio es requerida";
    if (!values.end_date) errors.end_date = "Fecha de fin es requerida";
    if (!values.link_group_whatsapp)
      errors.link_group_whatsapp = "Enlace Whatsapp es requerido";
    if (!values.course_id || values.course_id < 1)
      errors.course_id = "Seleccione un curso";
    if (!values.modality) errors.modality = "Seleccione una modalidad";
    if (!values.class_days || values.class_days.length === 0)
      errors.class_days = "Seleccione al menos un día";
    if (!values.class_schedule.start)
      errors.class_schedule = {
        ...errors.class_schedule,
        start: "Hora inicio requerida",
      };
    if (!values.class_schedule.end)
      errors.class_schedule = {
        ...errors.class_schedule,
        end: "Hora fin requerida",
      };
    if (values.class_schedule?.start && values.class_schedule?.end) {
      const [sh, sm] = values.class_schedule.start.split(":").map(Number);
      const [eh, em] = values.class_schedule.end.split(":").map(Number);
      const startMinutes = sh * 60 + sm;
      const endMinutes = eh * 60 + em;
      if (endMinutes <= startMinutes) {
        errors.class_schedule = {
          ...errors.class_schedule,
          end: "La hora fin debe ser mayor a la hora inicio",
        };
      }
    }
    if (!["0", "1", "2"].includes(values.status))
      errors.status = "Estado inválido";
    if (!values.order || values.order < 1)
      errors.order = "El orden debe ser mayor a 0";

    if (!isEdit) {
      const imageError = validateImage(values.image);
      if (imageError) errors.image = imageError;
    }
    return errors;
  };

  const clear = () => {
    setData({
      courses: {
        id: 0,
        name: "",
        code: "",
        n_modules: 0,
        n_academic_hours: 0,
      },
      course_id: 0,
      teachers: {
        id: 0,
        name: "",
        surname: "",
        dni: "",
      },
      teacher_id: 0,
      teacher: "",
      categories: {
        id: 0,
        name: "",
      },
      category_id: 0,
      category: "",
      description: "",
      start_date: today,
      end_date: "",
      link_group_whatsapp: "",
      link_form: "",
      modality: "Virtual",
      slots_available: 1,
      order: 1,
      class_days: [],
      class_schedule: {
        start: "",
        end: "",
      },
      status: "1",
      image: "",
    });
  };

  const onSubmit = async (values) => {
    let params: any = {
      course_id: values.course_id || 0,
      teacher_id: values.teacher_id || 0,
      teacher: values.teacher || "",
      category_id: values.category_id || 0,
      category: values.category || "",
      description: values.description,
      start_date: values.start_date,
      end_date: values.end_date,
      link_group_whatsapp: values.link_group_whatsapp,
      link_form: values.link_form,
      modality: values.modality,
      slots_available: values.slots_available,
      status: values.status || "1",
      image: values.image,
      class_days: values.class_days,
      class_schedule: values.class_schedule,
      order: values.order,
    };
    if (selectedCategory.id) {
      params = { ...params, category_id: selectedCategory.id };
    } else {
      params = { ...params, category: values.category };
    }
    const method = isEdit
      ? editCourseWebStore(editCourseWeb?.id || 0, params)
      : createCourseWebStore(params);
    const response = await method;
    if (response === true) {
      getCoursesWeb();
      setOpenModal(false);
    }
  };

  const onChangeCourse = (newValue: Course | null, setFieldValue: any) => {
    if (newValue) {
      setFieldValue("course_id", newValue.id);
      setSelectedCourse(newValue);
    } else {
      setFieldValue("course_id", 0);
      setSelectedCourse({} as Course);
    }
  };

  const onChangeTeacher = (newValue: Teacher | null, setFieldValue: any) => {
    if (newValue) {
      setFieldValue("teacher_id", newValue.id);
      setSelectedTeacher(newValue);
    } else {
      setFieldValue("teacher_id", "");
      setSelectedTeacher({} as Teacher);
    }
  };

  const onChangeCategory = (newValue: Category | null, setFieldValue: any) => {
    if (newValue) {
      setFieldValue("category_id", newValue.id);
      setSelectedCategory(newValue);
    } else {
      setFieldValue("category_id", "");
      setSelectedCategory({} as Category);
    }
  };

  return (
    <>
      <Modal
        open={openModal}
        handleClose={() => {
          clear();
          setOpenModal(false);
        }}
        disableEscapeKeyDown
        disableBackdropClick
        size="sm"
      >
        <ModalHeader
          text={isEdit ? "Editar Curso" : "Crear Curso"}
          className="positionElements"
          onCancel={() => {
            clear();
            setOpenModal(false);
          }}
        ></ModalHeader>

        <Formik
          initialValues={data}
          enableReinitialize
          validate={(values) => validateForm(values)}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            handleChange,
            setFieldValue,
            setFieldError,
          }) => {
            const preview = values.image
              ? typeof values.image === "string"
                ? values.image
                : URL.createObjectURL(values.image)
              : null;
            return (
              <>
                <ModalBody>
                  <form onSubmit={handleSubmit}>
                    <Grid container>
                      <Grid item container xs={12} spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12}>
                          <FormLabel>Curso:</FormLabel>
                          <Autocomplete
                            disablePortal
                            placeholder="Seleccione un curso"
                            value={selectedCourse}
                            options={courses}
                            getOptionLabel={(option) => {
                              if (!option || !option.code || !option.name)
                                return "";
                              return `${option.code} - ${option.name}`;
                            }}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={errors.course_id ? true : false}
                                helperText={
                                  errors.course_id ? errors.course_id : ""
                                }
                              />
                            )}
                            onChange={(e, newValue) =>
                              onChangeCourse(newValue, setFieldValue)
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Breve Descripción:</FormLabel>
                          <TextField
                            id="description"
                            name="description"
                            fullWidth
                            multiline
                            minRows={3}
                            maxRows={4}
                            size="small"
                            value={values.description}
                            onChange={handleChange}
                            error={
                              errors.description && touched.description
                                ? true
                                : false
                            }
                            helperText={
                              errors.description && touched.description
                                ? errors.description
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Categoría Existente:</FormLabel>
                          <Autocomplete
                            disablePortal
                            id="combo-box-category"
                            value={selectedCategory}
                            options={categories || []}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.name === value?.name
                            }
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={errors.category_id ? true : false}
                                helperText={
                                  errors.category_id && touched.category_id
                                    ? errors.category_id
                                    : ""
                                }
                              />
                            )}
                            onChange={(e, newValue) => {
                              onChangeCategory(newValue, setFieldValue);
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Categoría Nueva:</FormLabel>
                          <TextField
                            id="category"
                            type="text"
                            name="category"
                            fullWidth
                            size="small"
                            value={values.category}
                            onChange={handleChange}
                            error={
                              errors.category && touched.category ? true : false
                            }
                            helperText={
                              errors.category && touched.category
                                ? errors.category
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Profesor Existente:</FormLabel>
                          <Autocomplete
                            disablePortal
                            id="combo-box-teacher"
                            value={selectedTeacher}
                            options={teachers || []}
                            getOptionLabel={(option) => option.name || ""}
                            isOptionEqualToValue={(option, value) =>
                              option?.name === value?.name
                            }
                            fullWidth
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={errors.teacher_id ? true : false}
                                helperText={
                                  errors.teacher_id && touched.teacher_id
                                    ? errors.teacher_id
                                    : ""
                                }
                              />
                            )}
                            onChange={(e, newValue) => {
                              onChangeTeacher(newValue, setFieldValue);
                            }}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Profesor Nuevo:</FormLabel>
                          <TextField
                            id="teacher"
                            type="text"
                            name="teacher"
                            fullWidth
                            size="small"
                            value={values.teacher}
                            onChange={handleChange}
                            error={
                              errors.teacher && touched.teacher ? true : false
                            }
                            helperText={
                              errors.teacher && touched.teacher
                                ? errors.teacher
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Enlace Whatsapp:</FormLabel>
                          <TextField
                            id="link_group_whatsapp"
                            type="text"
                            name="link_group_whatsapp"
                            fullWidth
                            size="small"
                            value={values.link_group_whatsapp}
                            onChange={handleChange}
                            error={
                              errors.link_group_whatsapp &&
                              touched.link_group_whatsapp
                                ? true
                                : false
                            }
                            helperText={
                              errors.link_group_whatsapp &&
                              touched.link_group_whatsapp
                                ? errors.link_group_whatsapp
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>
                            Enlace Formulario de Inscripción:
                          </FormLabel>
                          <TextField
                            id="link_form"
                            type="text"
                            name="link_form"
                            fullWidth
                            size="small"
                            value={values.link_form}
                            onChange={handleChange}
                            error={
                              errors.link_form && touched.link_form
                                ? true
                                : false
                            }
                            helperText={
                              errors.link_form && touched.link_form
                                ? errors.link_form
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Modalidad:</FormLabel>
                          <TextField
                            select
                            id="modality"
                            name="modality"
                            fullWidth
                            size="small"
                            value={values.modality}
                            onChange={handleChange}
                            error={
                              errors.modality && touched.modality ? true : false
                            }
                            helperText={
                              errors.modality && touched.modality
                                ? errors.modality
                                : ""
                            }
                          >
                            <MenuItem value="Virtual">Virtual</MenuItem>
                            <MenuItem value="Presencial">Presencial</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Cupos Disponibles:</FormLabel>
                          <TextField
                            id="slots_available"
                            name="slots_available"
                            type="number"
                            fullWidth
                            size="small"
                            value={values.slots_available}
                            onChange={handleChange}
                            error={
                              errors.slots_available && touched.slots_available
                                ? true
                                : false
                            }
                            helperText={
                              errors.slots_available && touched.slots_available
                                ? errors.slots_available
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Fecha Inicio:</FormLabel>
                          <TextField
                            id="start_date"
                            name="start_date"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={values.start_date}
                            onChange={handleChange}
                            error={
                              errors.start_date && touched.start_date
                                ? true
                                : false
                            }
                            helperText={
                              errors.start_date && touched.start_date
                                ? errors.start_date
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Fecha Fin:</FormLabel>
                          <TextField
                            id="end_date"
                            name="end_date"
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={values.end_date}
                            onChange={handleChange}
                            error={
                              errors.end_date && touched.end_date ? true : false
                            }
                            helperText={
                              errors.end_date && touched.end_date
                                ? errors.end_date
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Días de clase:</FormLabel>
                          <Autocomplete
                            multiple
                            options={DAYS}
                            value={values.class_days}
                            onChange={(e, newValue) =>
                              setFieldValue("class_days", newValue)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                error={
                                  errors.class_days && touched.class_days
                                    ? true
                                    : false
                                }
                                helperText={
                                  errors.class_days && touched.class_days
                                    ? errors.class_days
                                    : ""
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Hora inicio:</FormLabel>
                          <TextField
                            id="class_schedule.start"
                            name="class_schedule.start"
                            type="time"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={values.class_schedule?.start || ""}
                            onChange={handleChange}
                            error={
                              errors.class_schedule?.start &&
                              touched.class_schedule?.start
                                ? true
                                : false
                            }
                            helperText={
                              errors.class_schedule?.start &&
                              touched.class_schedule?.start
                                ? errors.class_schedule.start
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormLabel>Hora fin:</FormLabel>
                          <TextField
                            id="class_schedule.end"
                            name="class_schedule.end"
                            type="time"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={values.class_schedule?.end || ""}
                            onChange={handleChange}
                            error={
                              errors.class_schedule?.end &&
                              touched.class_schedule?.end
                                ? true
                                : false
                            }
                            helperText={
                              errors.class_schedule?.end &&
                              touched.class_schedule?.end
                                ? errors.class_schedule.end
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Orden:</FormLabel>
                          <TextField
                            id="order"
                            name="order"
                            type="number"
                            fullWidth
                            size="small"
                            value={values.order}
                            onChange={handleChange}
                            error={errors.order && touched.order ? true : false}
                            helperText={
                              errors.order && touched.order ? errors.order : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Estado:</FormLabel>
                          <TextField
                            select
                            id="status"
                            name="status"
                            fullWidth
                            size="small"
                            value={values.status}
                            onChange={handleChange}
                            error={
                              errors.status && touched.status ? true : false
                            }
                            helperText={
                              errors.status && touched.status
                                ? errors.status
                                : ""
                            }
                          >
                            <MenuItem value="1">Activo</MenuItem>
                            <MenuItem value="2">Próximamente</MenuItem>
                            <MenuItem value="0">Inactivo</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <FormLabel>Imagen (Máx. 1000 KB):</FormLabel>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={8}>
                              <TextField
                                id="image"
                                type="file"
                                name="image"
                                fullWidth
                                size="small"
                                inputProps={{
                                  accept:
                                    "image/webp,image/png,image/jpeg,image/jpg",
                                }}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const file = event.target.files?.[0];
                                  if (!file) return;
                                  const error = validateImage(file);
                                  if (error) {
                                    setFieldError("image", error);
                                    setFieldValue("image", "");
                                    return;
                                  }
                                  setFieldValue("image", file);
                                  setFieldError("image", undefined);
                                }}
                                error={Boolean(errors.image && touched.image)}
                                helperText={
                                  errors.image && touched.image
                                    ? errors.image
                                    : ""
                                }
                              />
                            </Grid>
                            <Grid item xs={4}>
                              {preview ? (
                                <img
                                  src={preview}
                                  alt="Preview"
                                  loading="lazy"
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    objectFit: "cover",
                                    border: "1px solid #ddd",
                                    borderRadius: 4,
                                    backgroundColor: "#f5f5f5",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: "100%",
                                    height: 120,
                                    border: "1px dashed #ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#999",
                                    fontSize: 12,
                                  }}
                                >
                                  Sin imagen
                                </div>
                              )}
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </form>
                </ModalBody>
                <ModalFooter
                  confirmText={isEdit ? "Actualizar" : "Registrar"}
                  onConfirm={handleSubmit}
                  cancelText={"Cancelar"}
                  onCancel={() => {
                    setOpenModal(false);
                  }}
                  className="modal-confirm-footer"
                />
              </>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};
